/** In-app product shots — no live tenant required. */

export function OrcaAppMockup() {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#16141f] shadow-2xl shadow-black/50">
      <div className="flex items-center gap-2 border-b border-white/10 bg-[#100e16] px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="ml-2 truncate font-mono text-[10px] text-slate-500">orca-ai.slpmicrosystems.com</span>
      </div>
      <div className="flex min-h-[280px]">
        <aside className="w-28 shrink-0 border-r border-white/10 bg-[#100e16] p-2 text-[10px]">
          {[
            { label: 'Home', on: true },
            { label: 'Security', on: false },
            { label: 'Approvals', on: false },
            { label: 'SoD / UAR', on: false },
          ].map((item) => (
            <div
              key={item.label}
              className={`mb-1 rounded-md px-2 py-1.5 ${
                item.on ? 'bg-[#8b7cff]/25 text-[#ece9ff]' : 'text-slate-400'
              }`}
            >
              {item.label}
            </div>
          ))}
        </aside>
        <div className="min-w-0 flex-1 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-orca-volt">Ask Orca</p>
          <h3 className="mt-1 text-lg font-semibold text-white">Why can’t Jane see Compensation?</h3>
          <p className="mt-1 text-[11px] text-slate-400">Ask. Map the grant. Name the smallest fix.</p>
          <div className="mt-4 flex flex-wrap gap-2 text-[10px] font-medium">
            <span className="rounded-md border border-[#8b7cff]/80 px-2.5 py-1 text-[#d5d0ff]">Ask</span>
            <span className="rounded-md border border-[#8b7cff]/80 px-2.5 py-1 text-[#d5d0ff]">Map</span>
            <span className="rounded-md bg-orca-gold px-2.5 py-1 font-semibold">Fix</span>
            <span className="rounded-md border border-white/15 px-2.5 py-1 text-slate-400">Revoke</span>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg border border-white/10 bg-[#050c12] p-2.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Who</p>
              <p className="mt-1 text-xs font-semibold text-white">Jane Doe</p>
              <p className="text-[10px] text-slate-500">Compensation Manager · US LEG HR</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-[#050c12] p-2.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Blocking</p>
              <p className="mt-1 text-xs font-semibold text-orca-gold">Person security profile</p>
              <p className="text-[10px] text-slate-500">Privilege granted · team excluded</p>
            </div>
            <div className="rounded-lg border border-[#8b7cff]/25 bg-[#8b7cff]/10 p-2.5 sm:col-span-2">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-orca-volt">Next step</p>
              <p className="mt-1 text-[11px] text-slate-200">
                Widen Jane’s AOR — or send the stale Comp roles to Access Review
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AccessMapShot() {
  return (
    <div className="overflow-hidden rounded-xl border border-cyan-900/40 bg-[#02040a] text-left shadow-2xl">
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-cyan-300/80">User Lookup · Access map</span>
        <span className="rounded-full bg-emerald-900/50 px-2 py-0.5 text-[9px] text-emerald-200">jane.doe</span>
      </div>
      <div className="grid grid-cols-3 gap-px bg-white/5 text-[10px]">
        {[
          { h: 'Job / abstract', rows: ['Compensation Manager', 'Employee', 'Line Manager'] },
          { h: 'Privileges', rows: ['Manage Comp Workbench', 'View Salary', 'Approve Award'] },
          { h: 'Data / AOR', rows: ['US LEG HR', 'View All — no', 'AOR: Sales West'] },
        ].map((col) => (
          <div key={col.h} className="bg-[#050a14] p-2">
            <div className="mb-1.5 font-semibold text-slate-400">{col.h}</div>
            {col.rows.map((r) => (
              <div key={r} className="mb-1 truncate rounded bg-cyan-950/40 px-1.5 py-1 text-slate-200">
                {r}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="border-t border-white/5 px-3 py-2 text-[11px] text-amber-100/90">
        Blocking policy: Compensation Workbench privilege is granted, but person security profile excludes Jane’s team.
      </div>
    </div>
  );
}

export function ApprovalShot() {
  return (
    <div className="overflow-hidden rounded-xl border border-cyan-900/40 bg-[#02040a] text-left shadow-2xl">
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-cyan-300/80">Approval Designer</span>
        <span className="text-[9px] text-slate-500">Simulate · Draft</span>
      </div>
      <div className="flex items-center gap-2 p-3 text-[10px]">
        <Node label="Worker" sub="Promotion" />
        <span className="text-slate-600">→</span>
        <Node label="If salary > grade max + 5%" sub="Condition" accent />
        <span className="text-slate-600">→</span>
        <Node label="VP HR" sub="Stage 1" />
        <span className="text-slate-600">→</span>
        <Node label="CFO" sub="Stage 2" />
      </div>
      <div className="border-t border-white/5 px-3 py-2 text-[11px] text-emerald-200/90">
        Simulation on person 100234: Line Manager skip · VP HR · CFO. Sequential stages only in v1.
      </div>
    </div>
  );
}

function Node({ label, sub, accent }) {
  return (
    <div
      className={`min-w-0 flex-1 rounded-lg border px-2 py-1.5 ${
        accent ? 'border-cyan-700/60 bg-cyan-950/50' : 'border-white/10 bg-[#050a14]'
      }`}
    >
      <div className="truncate font-medium text-slate-100">{label}</div>
      <div className="truncate text-slate-500">{sub}</div>
    </div>
  );
}

export function SodShot() {
  return (
    <div className="overflow-hidden rounded-xl border border-cyan-900/40 bg-[#02040a] text-left shadow-2xl">
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-rose-300/80">SoD · Access Review</span>
        <span className="rounded-full bg-rose-900/50 px-2 py-0.5 text-[9px] text-rose-100">High</span>
      </div>
      <div className="space-y-1.5 p-3 text-[11px]">
        <div className="rounded-lg border border-rose-900/40 bg-rose-950/30 px-2 py-1.5 text-rose-100">
          Payroll Fraud Risk — PER_HIRE_EMPLOYEE_PRIV + PER_PROCESS_PAYROLL_PRIV
        </div>
        <div className="flex justify-between text-slate-400">
          <span>Campaign: US Comp + Payroll</span>
          <span>12 pending · Revoke sync to Oracle</span>
        </div>
      </div>
    </div>
  );
}
